import { createComponent as _$createComponent } from "solid-js/web";
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
  * @class mmodOfficeWfTzRecords
  * @extends mmodSolidWorkflow
  * @description
  *
  * # Purpose
  * mOffice module for manually imported TZ records (offline technical devices - TruCAM laser
  * guns today, other devices tomorrow). First production module built on the Solid stack —
  * no mele elements, the view is plain Solid JSX and the module's reactive store is the
  * single source of truth.
  *
  * The generic workflow behavior (protocol lifecycle, record opening, model-driven action
  * dispatch, translations, modals) comes from mmodSolidWorkflow/mmodSolidModule. This class
  * holds only the TZ-specific business logic; the views live under internal/:
  *
  *   internal/screen.jsx  - toolbar + live table + detail drawer host
  *   internal/drawer.jsx  - record detail (tabs Detail / Photos / History)
  *   internal/wizard.jsx  - the five-step import wizard (design: tmp/tzrecords-wizard-mock.html)
  *   internal/bulk.jsx    - bulk import overlay (pairs matched by file name)
  *
  * Records are created and edited through the wizard (IMPORT → DRAFT → CONFIRM); files
  * dropped anywhere in the module arrive via `mmFilesDropped` (forwarded by mmodOfficeImport).
  */

import mmodSolidWorkflow, { WorkflowGate } from 'mmodSolidWorkflow';
import { ModuleRoot, ModalHost, Lightbox } from 'mmodSolidModule/components';
import { Show } from 'solid-js';
import { WIZARD_STEPS, VIOLATION_TYPES } from './internal/constants';
import { OfficeScreen } from './internal/screen';
import { Wizard } from './internal/wizard';
import { BulkImport } from './internal/bulk';
import { belongsToMetadata, groupBulkEntries, sortTzEntries, validateSingleTzEntries } from './internal/bulk_pairing';
import { decodeUpload } from './internal/decode_upload';
import './mmodOfficeWfTzRecords.solid.scss';
const {
  parseMeta,
  detectSerials
} = require('../../../msvr/SK/msvrSkWfTzRecords/parsers/index.js');
export default class mmodOfficeWfTzRecords extends mmodSolidWorkflow {
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([], dependencies);
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: {
          type: 'full'
        },
        description: 'Initial window setup; same structure as required by `mmWindowSet`'
      },
      workflow_server: {
        type: 'string',
        default: ':msvrTzRecords',
        description: 'Listener acting as the server-side for the workflow'
      },
      limit: {
        type: 'integer',
        default: 100,
        description: 'Records per server page (first page live-watched, more load on scroll)'
      },
      sort: {
        type: 'array',
        items: {
          type: 'string'
        },
        default: ['event_dt-'],
        description: 'Server-side sort (newest passage first)'
      },
      list_fields: {
        type: 'array',
        items: {
          type: 'string'
        },
        description: 'fields served in list rows - EXACTLY what the table renders; everything else (photos, files, geocode, transfer details) comes from the targeted full fetch when a record opens',
        default: ['device_id', 'type', 'event_dt', 'lpnumber', 'speed_kmh', 'speed_limit', 'locality', 'state', 'jpeg3', 'import_user_id', 'import_login', 'created_dt']
      },
      exclude_device_types: {
        type: 'array',
        items: {
          type: 'string'
        },
        default: ['mDoppler', 'mBox'],
        description: 'device types NOT offered for manual assignment - the online devices whose records arrive by themselves'
      },
      max_photo_bytes: {
        type: 'integer',
        description: 'largest accepted evidence photo (decoded bytes); a photo above this is refused at import',
        default: 15_000_000
      },
      max_file_bytes: {
        type: 'integer',
        description: 'largest accepted extra vendor attachment (decoded bytes)',
        default: 15_000_000
      },
      meta_extensions: {
        type: 'array',
        items: {
          type: 'string'
        },
        default: ['.txt'],
        description: 'file extensions classified as the vendor METADATA file (new radar format = config entry, no build); an undeclared format still works via "use as metadata" on its file row + the parserless manual flow'
      },
      device_types: {
        type: 'array',
        items: {
          type: 'string'
        },
        default: ['trucam'],
        description: 'device types offered in the wizard parser dropdown (wf_sktmdevices type codes); the FIRST is the default parser for packages whose device the content detection could not identify'
      },
      confidence: {
        type: 'object',
        description: 'reported confidence values, fixed by configuration (the wizard shows them read-only); the server validates them again',
        default: {
          lpn: 90,
          country: 90
        }
      },
      default_lpcountry: {
        type: 'string',
        default: 'SK',
        description: 'license plate country preselected in the wizard; the operator can still change it'
      },
      preview: {
        type: 'object',
        description: 'downscale sizes for the stored preview (jpeg2) and table thumbnail (jpeg3)',
        default: {
          jpeg2: {
            width: 480,
            height: 270
          },
          jpeg3: {
            width: 240,
            height: 135
          }
        }
      },
      map: {
        type: 'object',
        description: 'basemap for the location step; same shape as one mmodSkMap maps entry (url = msysProxy repo)',
        default: {
          url: 'map_satellite',
          format: 'jpeg',
          maxzoom: 19,
          minzoom: 8
        }
      }
    }, cfg_schema);
    super(id, cfg, cfg_schema, dependencies);
    // appbar identity comes the plain way: icons.htm has the mmodOfficeTzRecords icon,
    // lang.csv the mmodOfficeTzRecords menu label — nothing model-driven
    // ---- view ----
    _defineProperty(this, "view", () => {
      const _self$ = this;
      return _$createComponent(ModuleRoot, {
        "class": "mmod-tzr",
        get children() {
          return [_$createComponent(WorkflowGate, {
            get children() {
              return _$createComponent(OfficeScreen, {});
            }
          }), _$createComponent(Show, {
            get when() {
              return _self$.store.wizard;
            },
            get children() {
              return _$createComponent(Wizard, {});
            }
          }), _$createComponent(Show, {
            get when() {
              return _self$.store.bulk;
            },
            get children() {
              return _$createComponent(BulkImport, {});
            }
          }), _$createComponent(ModalHost, {}), _$createComponent(Lightbox, {
            get src() {
              return _self$.store.lightbox;
            },
            onClose: () => _self$.setStore('lightbox', '')
          })];
        }
      });
    });
  }
  initialStore() {
    return {
      ...super.initialStore(),
      detail_geocode: null,
      // territorial names for the drawer (record stores only codes)
      lightbox: '',
      // enlarged image source, '' = closed
      search: '',
      // quick client-side search over the loaded rows
      sort: {
        key: 'event_dt',
        dir: -1
      },
      // active column sort (mirrors the config default)
      filters: {},
      // server-side filters: state, mine, device_id, lpnumber, locality, created_dt/event_dt {from,to}
      devices: null,
      // TM register serials (filter dropdown + wizard manual assignment)
      tm_devices: null,
      // the same register rows with their device types, used by local parsing
      wizard: null,
      // closed; open = { step, record, values, files, geocode, busy, error, notice, crop_preview }
      bulk: null // closed; open = { entries, files: {basename: {photos, extras, meta, meta_name}}, shared, results, running }
    };
  }
  mmExit(sender, params) {
    if (this.map_listener) {
      this.map_listener.publish('#ME', 'mmExit');
      delete this.map_listener;
    }
    super.mmExit(sender, params);
  }

  /** The FULL start runs on login - also remember who we are for the "Mine" chips/filter. */
  async mmUserLogged(sender, params) {
    this.user_id = _.natural(params.user_id) || 0;
    await super.mmUserLogged(sender, params);
  }

  /** Imported-by cell text: the login the SERVER stamped into the record (import_login,
   *  <surname><personal number>). Records from before the stamp existed (and not reached
   *  by the SQL backfill) fall back to the register's LANG options by user id - never
   *  the internal user id itself. */
  importerLabel(record) {
    if (record.import_login) return record.import_login;
    const user_id = record.import_user_id;
    if (!user_id) return '—';
    const login = this.t(`user.login.${user_id}`);
    if (!login.startsWith('user.login.')) return login;
    const full = this.t(`user.fullname.${user_id}`);
    return full.startsWith('user.fullname.') ? '—' : full;
  }

  /** Hover title for the Imported-by cell - the full name behind the login. */
  importerTitle(user_id) {
    if (!user_id) return '';
    const full = this.t(`user.fullname.${user_id}`);
    return full.startsWith('user.fullname.') ? '' : full;
  }

  /**
  * @description
  * Files dropped anywhere in this module, forwarded by mmodOfficeImport. The bulk overlay
  * takes them when open; otherwise they open the wizard and feed its first step.
  */
  async mmFilesDropped(sender, params) {
    const files = Array.isArray(params === null || params === void 0 ? void 0 : params.files) ? params.files : [];
    if (files.length === 0) return;
    if (this.store.bulk) return this.bulkTakeFiles(files);
    if (!this.store.wizard) this.openWizard(null);
    await this.takeFiles(files);
  }

  // ---- filters (bosy mmodOfficeWorkflow pattern: quick popover + advanced drawer + chips) ----

  /** Toolbar chip presets over state + ownership: All | Mine | My drafts | transfer states
   *  (a chip is a {state?, mine?} pair; no bare DRAFT chip - own drafts cover it). */
  listChips() {
    return [{
      label: this.tr('list.All')
    }, {
      mine: true,
      label: this.tr('list.Mine')
    }, {
      mine: true,
      state: 'DRAFT',
      label: this.tr('list.MineDrafts')
    }, {
      state: 'READY',
      label: this.tr('state.READY')
    }, {
      state: 'FORWARDED',
      label: this.tr('state.FORWARDED')
    }, {
      state: 'NOTFORWARDED',
      label: this.tr('state.NOTFORWARDED')
    }];
  }

  /** One chip click sets both underlying filters (state + mine) in a single re-query. */
  setChip(chip) {
    this.setStore('filters', {
      ...this.store.filters,
      state: chip.state,
      mine: chip.mine ? '1' : undefined
    });
    this.applyFilters();
  }
  chipSelected(chip) {
    const filters = this.store.filters;
    return (filters.state || '') === (chip.state || '') && !!filters.mine === !!chip.mine;
  }

  /** The filter surface, hand-declared (the model's mele filter form was retired with the forms). */
  filterItems() {
    var _this$wfclient$slice$;
    const states = Object.keys(((_this$wfclient$slice$ = this.wfclient.slice().model) === null || _this$wfclient$slice$ === void 0 ? void 0 : _this$wfclient$slice$.states) || {}).filter(state => state !== '' && state !== 'WIPED');
    return [{
      key: 'state',
      kind: 'select',
      quick: true,
      options: states.map(state => ({
        value: state,
        label: this.tr(`state.${state}`)
      }))
    }, {
      key: 'created_dt',
      kind: 'range',
      quick: true
    },
    // when the record entered OUR db - the default date filter
    {
      key: 'event_dt',
      kind: 'range'
    }, {
      key: 'mine',
      kind: 'select',
      options: [{
        value: '1',
        label: this.tr('filter.Mine')
      }]
    }, {
      key: 'type',
      kind: 'select',
      options: VIOLATION_TYPES.map(type => ({
        value: type,
        label: this.tr(`violation.${type}`)
      }))
    }, {
      key: 'device_id',
      kind: 'select',
      options: (this.store.devices || []).map(id => ({
        value: id,
        label: id
      }))
    }, {
      key: 'lpnumber',
      kind: 'text'
    }, {
      key: 'locality',
      kind: 'text'
    }];
  }

  /** The filters with a value set (drives the chips row + the Filter·N badge). */
  activeFilters() {
    const filters = this.store.filters;
    return this.filterItems().map(item => ({
      item,
      value: filters[item.key]
    })).filter(({
      item,
      value
    }) => {
      if (item.kind === 'range') return !!(value && (value.from || value.to));
      return value != null && value !== '';
    });
  }

  /** Compile the active filters into the prefixed server query (mosy search operators). */
  filterQuery() {
    const query = {};
    for (const {
      item,
      value
    } of this.activeFilters()) {
      if (item.kind === 'range') {
        // date inputs give YYYY-MM-DD; the workflow field is µs — whole-day bounds
        if (value.from) query[`>=${item.key}`] = Date.parse(value.from) * 1000;
        if (value.to) query[`<=${item.key}`] = (Date.parse(value.to) + 86_399_999) * 1000;
      } else if (item.key === 'mine') {
        query.import_user_id = _.toString(this.user_id); // "mine" = imported by the logged-in user
      } else if (item.kind === 'select') {
        query[item.key] = value;
      } else {
        query[`~${item.key}`] = value; // case-insensitive contains
      }
    }
    return query;
  }

  /** NB solid's setStore MERGES objects — removal must set the key to undefined. */
  setFilter(key, value) {
    this.setStore('filters', {
      ...this.store.filters,
      [key]: value
    });
    this.applyFilters();
  }
  clearFilters() {
    this.setStore('filters', Object.fromEntries(Object.keys(this.store.filters).map(key => [key, undefined])));
    this.applyFilters();
  }
  applyFilters() {
    this.wfclient.setQuery({
      ...this.config.query,
      ...this.filterQuery()
    });
  }

  /** Server-side column sort — with infinite scroll only the server sees all matching rows. */
  toggleSort(key) {
    const current = this.store.sort;
    const dir = current.key === key ? -current.dir : 1;
    this.setStore('sort', {
      key,
      dir
    });
    this.wfclient.setSort([`${key}${dir < 0 ? '-' : ''}`]);
  }

  // ---- mmodSolidWorkflow hooks ----

  /** The opened record arrived fully loaded — look up its territorial names for the drawer. */
  recordOpened(record) {
    this.setStore('detail_geocode', null);
    this.detailGeocode(record);
  }

  /** The wizard replaces the framework editors for the import and edit forms. */
  async openForm(form, record) {
    if (form === 'import') {
      this.openWizard(null);
      return true;
    }
    if (form === 'edit') {
      this.openWizard(record);
      return true;
    }
    return false;
  }

  /** EXPORT_TRM downloads the exact SavePassageDataRequest body locally. */
  async customAction(record, action) {
    if (action === 'EXPORT_TRM') {
      this.exportTrmLocal(record.id);
      return true;
    }
    return false;
  }

  // ---- geocode lookups (mmGeocode on the workflow server) ----

  /** Territorial names for the drawer — the record stores only the codes. */
  async detailGeocode(record) {
    const lat = Number(record === null || record === void 0 ? void 0 : record.gps_lat);
    const lon = Number(record === null || record === void 0 ? void 0 : record.gps_lon);
    if (!(Number.isFinite(lat) && Number.isFinite(lon)) || lat === 0 && lon === 0) return;
    try {
      const reply = await this.request(this.config.workflow_server, 'mmGeocode', {
        lat,
        lon
      }, ['mmGeocoded']);
      if (this.store.selected === record.id) this.setStore('detail_geocode', reply.params);
    } catch (e) {
      // no geocode for the drawer is cosmetic — codes still show
    }
  }

  /**
  * @description
  * Live preview of the territorial classification (kraj/okres/obec) for the entered GPS —
  * the same GIS lookup the CONFIRM gate runs, so the operator sees geocode problems early.
  */
  async geocodePreview() {
    var _w$geocode, _w$geocode2;
    const lat = Number(this.value('gps_lat'));
    const lon = Number(this.value('gps_lon'));
    if (!(Number.isFinite(lat) && Number.isFinite(lon)) || lat === 0 && lon === 0) return;
    const w = this.store.wizard;
    if ((w === null || w === void 0 ? void 0 : (_w$geocode = w.geocode) === null || _w$geocode === void 0 ? void 0 : _w$geocode._lat) === lat && (w === null || w === void 0 ? void 0 : (_w$geocode2 = w.geocode) === null || _w$geocode2 === void 0 ? void 0 : _w$geocode2._lon) === lon) return; // unchanged position
    try {
      const reply = await this.request(this.config.workflow_server, 'mmGeocode', {
        lat,
        lon
      }, ['mmGeocoded']);
      if (this.store.wizard) {
        this.setStore('wizard', 'geocode', {
          ...reply.params,
          _lat: lat,
          _lon: lon
        });
        // best prefill our data offers for the location description: the municipality name
        // (no street-level data in the GIS schema); never overwrites operator text
        if (!this.value('locality') && reply.params.municipality_name) {
          this.setValue('locality', reply.params.municipality_name);
        }
      }
    } catch (e) {
      var _e$params;
      if (this.store.wizard) this.setStore('wizard', 'geocode', {
        error: this.t(_.toString((e === null || e === void 0 ? void 0 : (_e$params = e.params) === null || _e$params === void 0 ? void 0 : _e$params.error) || 'wf.sktzrecords.error.NoGeocode')),
        _lat: lat,
        _lon: lon
      });
    }
  }

  // ---- wizard state ----

  /**
  * @description
  * Opens the wizard at the files step for both a new import and an existing DRAFT.
  */
  openWizard(record) {
    this.setStore('wizard', null); // reset first — solid merges objects on set
    this.setStore('wizard', {
      step: 0,
      record: record ? _.cloneDeep(record) : null,
      new_record: !record,
      values: _.defaults(record ? this.valuesFromRecord(record) : {}, {
        lane: '1',
        device_type: this.config.device_types[0] || '',
        lpcountry: this.config.default_lpcountry,
        license_plate_number_confidence: this.config.confidence.lpn,
        country_code_confidence: this.config.confidence.country
      }),
      files: {
        jpeg: '',
        meta: '',
        photo_name: (record === null || record === void 0 ? void 0 : record.photo_name) || '',
        meta_name: (record === null || record === void 0 ? void 0 : record.meta_name) || '',
        photos: [],
        // all JPEGs available to step 2; the first is provisional until selected there
        extras: [] // vendor files kept locally until the complete package is explicitly saved
      },
      geocode: null,
      // territorial preview from mmGeocode
      busy: '',
      error: '',
      error_raw: '',
      // untranslated server error - carries the record id of an AlreadyImported abort
      notice: '',
      // informational banner (e.g. resumed an already-imported package)
      crop_preview: (record === null || record === void 0 ? void 0 : record.lpjpeg) || '',
      selected_photo_name: (record === null || record === void 0 ? void 0 : record.photo_name) || '' // explicit operator choice in step 2
    });
    if (record !== null && record !== void 0 && record.id) this.loadWizardPhotos();
    // resuming a DRAFT: the passed record is usually the slim list row — pull the photos in
    if (record !== null && record !== void 0 && record.id && !record.jpeg) this.hydrateWizard(record.id);
    // a draft without a device needs the register dropdown filled (the device may have been
    // registered only after the import) — and the serial the metadata named preselected
    if (record && !record.device_id) this.offerDevices(record);
  }

  /**
  * @description
  * The operator changed the parser type (the "typ zariadenia" dropdown above the parsed
  * metadata). Pre-import it just rides the wizard values into IMPORT; on an existing DRAFT
  * it saves immediately so the server re-runs the matching parser and the panel refreshes.
  */
  async changeParserType(type) {
    var _w$record;
    this.setValue('device_type', type);
    const w = this.store.wizard;
    if (!(w !== null && w !== void 0 && (_w$record = w.record) !== null && _w$record !== void 0 && _w$record.id)) {
      await this.prepareStagedRecord();
      return;
    }
    const updated = await this.wizardAction(w.record.id, 'UPDATE', {
      ...w.values
    });
    if (updated && this.store.wizard) {
      this.setStore('wizard', 'record', updated);
      // reparse prefilled only fields the operator left empty - pull those in
      this.setStore('wizard', 'values', _.defaults({}, this.store.wizard.values, this.valuesFromRecord(updated)));
    }
  }

  /**
  * @description
  * Fills the device dropdown for a device-less draft and preselects the serial the metadata
  * file named, if it has appeared in the register since the import.
  */
  async offerDevices(record) {
    var _exec;
    await this.loadDevices();
    if (!this.store.wizard || this.value('device_id')) return;
    const detected = (_exec = /DeviceNotInRegister}}: *(\S+)/.exec((record.parse_warnings || []).join('\n'))) === null || _exec === void 0 ? void 0 : _exec[1];
    if (!detected) return;
    const wanted = detected.replace(/\s+/g, '').toLowerCase();
    const match = (this.store.devices || []).find(id => id.replace(/\s+/g, '').toLowerCase() === wanted);
    if (match) this.setValue('device_id', match);
  }
  closeWizard() {
    this.setStore('wizard', null);
  }

  /** Refreshes the wizard from the fully-fetched record (photos, crops, parser output). */
  async hydrateWizard(id) {
    try {
      var _w$record2;
      const full = await this.wfclient.fetchFull(id);
      const w = this.store.wizard;
      if (!full || !w || ((_w$record2 = w.record) === null || _w$record2 === void 0 ? void 0 : _w$record2.id) !== id) return; // wizard closed or moved on meanwhile
      this.setStore('wizard', 'record', full);
      // prefill from the full record, but never clobber values the user already edited
      this.setStore('wizard', 'values', _.defaults({}, w.values, this.valuesFromRecord(full)));
      if (full.lpjpeg && !w.crop_preview) this.setStore('wizard', 'crop_preview', full.lpjpeg);
      if (!w.selected_photo_name && full.photo_name) this.setStore('wizard', 'selected_photo_name', full.photo_name);
      await this.loadWizardPhotos();
    } catch (e) {
      var _e$params2;
      this.setStore('wizard', 'error', _.toString(e !== null && e !== void 0 && (_e$params2 = e.params) !== null && _e$params2 !== void 0 && _e$params2.error ? this.t(e.params.error) : (e === null || e === void 0 ? void 0 : e.message) || e));
    }
  }

  /** Editable wizard values keyed by workflow field names, prefilled from a record. */
  valuesFromRecord(record) {
    const values = {};
    for (const key of ['device_id', 'device_type', 'lpnumber', 'license_plate_number_confidence', 'lpcountry', 'country_code_confidence', 'type', 'event_dt', 'speed_kmh', 'speed_limit', 'lane', 'employee_num', 'locality', 'context', 'gps_lat', 'gps_lon']) {
      if (record[key] != null && record[key] !== '') values[key] = record[key];
    }
    if (record.lpcrop) values.lpcrop = typeof record.lpcrop === 'string' ? record.lpcrop : JSON.stringify(record.lpcrop);
    if (record.lpjpeg) values.lpjpeg = record.lpjpeg;
    return values;
  }
  setValue(key, value) {
    this.setStore('wizard', 'values', key, value);
  }

  /** Raw stored value - inputs bind to it verbatim; a trimmed value here would eat the
   *  space the operator just typed (the input re-renders from the store on every key). */
  value(key) {
    var _this$store$wizard, _this$store$wizard$va;
    return _.toString((_this$store$wizard = this.store.wizard) === null || _this$store$wizard === void 0 ? void 0 : (_this$store$wizard$va = _this$store$wizard.values) === null || _this$store$wizard$va === void 0 ? void 0 : _this$store$wizard$va[key]);
  }

  /** Completeness test for the step gates - whitespace is not a value. */
  filled(key) {
    return this.value(key).trim() !== '';
  }

  /** Violation type drives which detail fields apply: the lane belongs to a speed
   *  measurement only, so it is cleared (and its default restored) on a type switch. */
  setViolationType(type) {
    this.setValue('type', type);
    this.setValue('lane', type === 'SPD' ? this.value('lane').trim() || '1' : '');
  }

  /** Values as sent to the server: typing artifacts (edge whitespace) removed. */
  trimmedValues(values) {
    return _.mapValues(values, value => typeof value === 'string' ? value.trim() : value);
  }

  /** Per-step completeness gate deciding whether Next (or a forward jump) is allowed. */
  stepGate(index) {
    const w = this.store.wizard;
    if (!w) return false;
    switch (WIZARD_STEPS[index]) {
      // the record must exist AND have a device: either recognised in the register at import,
      // or assigned manually from the register dropdown — without it CONFIRM can only fail
      case 'tz_files':
        return !!w.record && !!(w.record.device_id || this.filled('device_id'));
      case 'tz_vehicle':
        {
          const lpn_confidence = Number(this.value('license_plate_number_confidence'));
          const country_confidence = Number(this.value('country_code_confidence'));
          return this.filled('lpcrop') && this.filled('lpnumber') && this.filled('lpcountry') && Number.isInteger(lpn_confidence) && lpn_confidence >= 1 && lpn_confidence <= 100 && Number.isInteger(country_confidence) && country_confidence >= 1 && country_confidence <= 100;
        }
      case 'tz_violation':
        {
          const type = this.value('type');
          if (!type || !this.filled('event_dt')) return false;
          return type !== 'SPD' || this.filled('speed_kmh') && this.filled('speed_limit');
        }
      case 'tz_location':
        return this.filled('locality') && this.filled('context') && this.filled('gps_lat') && this.filled('gps_lon');
      default:
        return true;
    }
  }
  allGatesPass() {
    return WIZARD_STEPS.every((s, i) => this.stepGate(i));
  }
  showStep(index) {
    if (!this.store.wizard) return;
    index = Math.min(WIZARD_STEPS.length - 1, Math.max(0, index));
    // steps already passed can always be revisited; forward jumps go through the gate
    if (index > this.store.wizard.step && !this.stepGate(this.store.wizard.step)) return;
    this.setStore('wizard', 'step', index);
    if (index === 1) this.loadWizardPhotos();
    if (index >= 3) this.geocodePreview(); // location + summary show the territorial codes
  }

  // ---- files (wizard step 1) ----

  /**
  * @description
  * Reads picked/forwarded files into typed entries. Accepts File objects from a picker or
  * already-read `{name, mime, data}` entries forwarded by mmodOfficeImport. Anything that is
  * neither the photo nor the metadata file is an extra vendor attachment (JMX/JMF, future
  * device files) - accepted as-is.
  * @returns {Array<{name: string, mime: string, is_meta: boolean, is_jpeg: boolean, data_url: string}>}
  */
  async readDropEntries(files) {
    const readDataUrl = file => new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve('');
      reader.readAsDataURL(file);
    });
    const meta_extensions = (this.config.meta_extensions || ['.txt']).map(ext => _.toString(ext).toLowerCase());
    const entries = [];
    for (const file of files || []) {
      var _ref, _file$mime;
      const mime = (_ref = (_file$mime = file.mime) !== null && _file$mime !== void 0 ? _file$mime : file.type) !== null && _ref !== void 0 ? _ref : '';
      const extension = ('.' + _.toString(file.name).split('.').pop()).toLowerCase();
      const is_meta = meta_extensions.includes(extension);
      const is_jpeg = /\.jpe?g$/i.test(file.name) || mime === 'image/jpeg';
      const data_url = typeof file.data === 'string' ? file.data : await readDataUrl(file);
      if (!data_url) continue;
      entries.push({
        name: file.name,
        mime: _.toString(mime),
        is_meta,
        is_jpeg,
        data_url
      });
    }
    return entries;
  }

  /**
  * @description
  * Routes dropped/picked files through one shared pipeline. Before a record exists everything
  * stays local until explicit save/submit. Only an already persisted DRAFT uses ATTACH.
  */
  async takeFiles(files) {
    var _w$record3;
    const w = this.store.wizard;
    if (!w) return;
    const additions = await this.readDropEntries(files);
    if ((_w$record3 = w.record) !== null && _w$record3 !== void 0 && _w$record3.id) return this.attachToDraft(additions);

    // Picker and drag&drop deliberately enter the same deterministic pipeline. Keep all files
    // staged until the one-TXT package has been validated, so a rejected selection cannot leave
    // half of a different TZ mixed into the wizard.
    const existing = [];
    if (w.files.meta_name) {
      existing.push({
        name: w.files.meta_name,
        mime: 'text/plain',
        is_meta: true,
        is_jpeg: false,
        data_url: w.files.meta
      });
    }
    existing.push(...(w.files.photos || []), ...(w.files.extras || []));
    const by_name = new Map(existing.map(entry => [entry.name.toLowerCase(), entry]));
    for (const entry of additions) by_name.set(entry.name.toLowerCase(), entry);
    const entries = sortTzEntries([...by_name.values()]);
    const metadata = entries.filter(entry => entry.is_meta);
    const invalid = validateSingleTzEntries(entries);
    if (invalid) {
      const files = invalid.error === 'error.PhotoNameMismatch' ? `: ${invalid.files.join(', ')}` : '';
      this.setStore('wizard', 'error', `${this.tr(invalid.error)}${files}`);
      return;
    }
    const photos = entries.filter(entry => entry.is_jpeg);
    const extras = entries.filter(entry => !entry.is_meta && !entry.is_jpeg);
    for (const entry of photos) {
      if (entry.is_jpeg && this.uploadBytes(entry.data_url) > this.config.max_photo_bytes) {
        this.setStore('wizard', 'error', `${this.tr('error.InvalidPhoto')}: ${entry.name}`);
        return;
      }
    }
    for (const entry of extras) {
      if (this.uploadBytes(entry.data_url) > this.config.max_file_bytes) {
        this.setStore('wizard', 'error', `${this.tr('error.InvalidAttachment')}: ${entry.name}`);
        return;
      }
    }
    const meta = metadata[0];
    const main = photos.find(photo => photo.name === w.selected_photo_name) || photos[0];
    this.setStore('wizard', 'error', '');
    this.setStore('wizard', 'files', {
      meta: (meta === null || meta === void 0 ? void 0 : meta.data_url) || '',
      meta_name: (meta === null || meta === void 0 ? void 0 : meta.name) || '',
      jpeg: (main === null || main === void 0 ? void 0 : main.data_url) || '',
      photo_name: (main === null || main === void 0 ? void 0 : main.name) || '',
      photos,
      extras
    });
    await this.prepareStagedRecord();
  }

  /** One ATTACH/DETACH on the wizard's record; the echoed record refreshes the wizard. */
  async fileAction(action, params) {
    var _w$record4;
    const w = this.store.wizard;
    if (!(w !== null && w !== void 0 && (_w$record4 = w.record) !== null && _w$record4 !== void 0 && _w$record4.id)) return null;
    const updated = await this.wizardAction(w.record.id, action, params);
    if (updated && this.store.wizard) this.setStore('wizard', 'record', updated);
    return updated;
  }

  /** Files dropped on an existing DRAFT - add photos/files or replace the metadata. */
  async attachToDraft(entries) {
    const wizard = this.store.wizard;
    const record = wizard === null || wizard === void 0 ? void 0 : wizard.record;
    if (wizard !== null && wizard !== void 0 && wizard.new_record) {
      const metadata = entries.filter(entry => entry.is_meta);
      if (metadata.some(entry => entry.name.toLowerCase() !== _.toString(record.meta_name).toLowerCase())) {
        this.setStore('wizard', 'error', this.tr('error.MultipleMetadata'));
        return;
      }
      const mismatched = entries.filter(entry => entry.is_jpeg && !belongsToMetadata(entry.name, record.meta_name));
      if (mismatched.length > 0) {
        this.setStore('wizard', 'error', `${this.tr('error.PhotoNameMismatch')}: ${mismatched.map(file => file.name).join(', ')}`);
        return;
      }
    }
    for (const entry of sortTzEntries(entries)) {
      var _this$store$wizard2;
      if (!((_this$store$wizard2 = this.store.wizard) !== null && _this$store$wizard2 !== void 0 && _this$store$wizard2.record)) return;
      const current_record = this.store.wizard.record;
      if (entry.name === current_record.photo_name || (current_record.attachments || []).some(file => file.name === entry.name)) {
        this.setStore('wizard', 'error', `${this.tr('error.InvalidAttachment')}: ${entry.name}`);
        continue;
      }
      const limit = entry.is_jpeg ? this.config.max_photo_bytes : this.config.max_file_bytes;
      if (this.uploadBytes(entry.data_url) > limit) {
        this.setStore('wizard', 'error', `${this.tr(entry.is_jpeg ? 'error.InvalidPhoto' : 'error.InvalidAttachment')}: ${entry.name}`);
        continue;
      }
      if (entry.is_jpeg) {
        const updated = await this.fileAction('ATTACH', {
          slot: 'attachment',
          name: entry.name,
          mime: 'image/jpeg',
          data: entry.data_url
        });
        if (updated) {
          const photos = this.store.wizard.files.photos || [];
          if (!photos.some(photo => photo.name === entry.name)) {
            this.setStore('wizard', 'files', 'photos', sortTzEntries([...photos, entry]));
          }
        }
      } else if (entry.is_meta) {
        const updated = await this.fileAction('ATTACH', {
          slot: 'meta',
          meta: entry.data_url,
          meta_name: entry.name
        });
        if (updated) {
          // reparse prefilled only fields the operator left empty - pull those in
          this.setStore('wizard', 'values', _.defaults({}, this.store.wizard.values, this.valuesFromRecord(updated)));
          this.setStore('wizard', 'files', {
            meta: entry.data_url,
            meta_name: entry.name
          });
        }
      } else {
        await this.fileAction('ATTACH', {
          slot: 'attachment',
          name: entry.name,
          mime: entry.mime,
          data: entry.data_url
        });
      }
    }
  }

  /**
  * @description
  * Promotes a QUEUED extra file into the metadata slot - the escape hatch for a vendor
  * format nobody declared in meta_extensions: the operator reclassifies it with one click
  * and the parserless manual flow takes it from there. Only queued (pre-import) files need
  * this - once a record exists, the metadata was classified correctly.
  */
  async useAsMeta(name) {
    var _w$record5;
    const w = this.store.wizard;
    if (!w || (_w$record5 = w.record) !== null && _w$record5 !== void 0 && _w$record5.id) return;
    const queued = (w.files.extras || []).find(entry => entry.name === name);
    if (!queued) return;
    this.setStore('wizard', 'files', {
      extras: w.files.extras.filter(entry => entry.name !== name),
      meta: queued.data_url,
      meta_name: queued.name
    });
    await this.prepareStagedRecord();
  }

  /** Removes one attachment - a queued extra before IMPORT, a DETACH on the record after. */
  async detachFile(name) {
    var _w$record6;
    const w = this.store.wizard;
    if (!w) return;
    if (!((_w$record6 = w.record) !== null && _w$record6 !== void 0 && _w$record6.id)) {
      const photos = (w.files.photos || []).filter(entry => entry.name !== name);
      const extras = (w.files.extras || []).filter(entry => entry.name !== name);
      const removed_meta = w.files.meta_name === name;
      const main = photos[0];
      this.setStore('wizard', 'files', {
        photos,
        extras,
        meta: removed_meta ? '' : w.files.meta,
        meta_name: removed_meta ? '' : w.files.meta_name,
        jpeg: (main === null || main === void 0 ? void 0 : main.data_url) || '',
        photo_name: (main === null || main === void 0 ? void 0 : main.name) || ''
      });
      this.setStore('wizard', {
        record: null,
        selected_photo_name: '',
        crop_preview: ''
      });
      this.setValue('lpcrop', '');
      this.setValue('lpjpeg', '');
      await this.prepareStagedRecord();
      return;
    }
    const updated = await this.fileAction('DETACH', {
      name
    });
    if (updated && this.store.wizard) {
      this.setStore('wizard', 'files', 'photos', (this.store.wizard.files.photos || []).filter(photo => photo.name !== name));
      // never leave the step-2 frame pointing at the removed photo - fall back to the
      // record's main photo, which is the one the plate region belongs to
      if (this.store.wizard.selected_photo_name === name) {
        var _this$store$wizard$re;
        this.setStore('wizard', 'selected_photo_name', ((_this$store$wizard$re = this.store.wizard.record) === null || _this$store$wizard$re === void 0 ? void 0 : _this$store$wizard$re.photo_name) || '');
      }
    }
  }

  /** Loads the main and attached JPEGs needed by the photo selector in step 2. */
  async loadWizardPhotos() {
    var _this$store$wizard3, _this$store$wizard3$r;
    const w = this.store.wizard;
    const record = w === null || w === void 0 ? void 0 : w.record;
    if (!(record !== null && record !== void 0 && record.id)) return;
    const known = new Map((w.files.photos || []).map(photo => [photo.name, photo]));
    if (record.photo_name && record.jpeg) {
      known.set(record.photo_name, {
        name: record.photo_name,
        mime: 'image/jpeg',
        is_jpeg: true,
        data_url: record.jpeg
      });
    }
    const missing = (record.attachments || []).filter(file => {
      var _known$get;
      return file.mime === 'image/jpeg' && !((_known$get = known.get(file.name)) !== null && _known$get !== void 0 && _known$get.data_url);
    });
    const loaded = await Promise.all(missing.map(async file => ({
      file,
      data_url: await this.readAttachedPhoto(record.id, file)
    })));
    for (const {
      file,
      data_url
    } of loaded) {
      if (data_url) known.set(file.name, {
        ...file,
        is_jpeg: true,
        data_url
      });
    }
    if (((_this$store$wizard3 = this.store.wizard) === null || _this$store$wizard3 === void 0 ? void 0 : (_this$store$wizard3$r = _this$store$wizard3.record) === null || _this$store$wizard3$r === void 0 ? void 0 : _this$store$wizard3$r.id) === record.id) {
      this.setStore('wizard', 'files', 'photos', [...known.values()]);
    }
  }

  /** Reads one stored JPEG attachment as a browser data URL. */
  async readAttachedPhoto(id, file) {
    try {
      var _reply$params, _reply$params$list, _reply$params$list$;
      const reply = await this.request(this.config.workflow_server, 'mmAction', {
        action: 'GET_FILE',
        id,
        params: {
          name: file.name
        }
      });
      const binary = reply === null || reply === void 0 ? void 0 : (_reply$params = reply.params) === null || _reply$params === void 0 ? void 0 : (_reply$params$list = _reply$params.list) === null || _reply$params$list === void 0 ? void 0 : (_reply$params$list$ = _reply$params$list[0]) === null || _reply$params$list$ === void 0 ? void 0 : _reply$params$list$.export;
      if (!binary || typeof binary === 'string') return '';
      return await new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = () => resolve(_.toString(reader.result));
        reader.onerror = () => resolve('');
        reader.readAsDataURL(new Blob([binary], {
          type: 'image/jpeg'
        }));
      });
    } catch (e) {
      return '';
    }
  }

  /** Makes one of the imported JPEGs main; selection belongs exclusively to wizard step 2. */
  async selectMainPhoto(name) {
    var _w$record7;
    const w = this.store.wizard;
    if (!w || w.busy) return;
    const photo = (w.files.photos || []).find(item => item.name === name);
    if (!(photo !== null && photo !== void 0 && photo.data_url)) return;
    if (!((_w$record7 = w.record) !== null && _w$record7 !== void 0 && _w$record7.id)) {
      const {
        jpeg2,
        jpeg3
      } = await this.createPreviews(photo.data_url);
      if (!this.store.wizard) return;
      this.setStore('wizard', 'files', {
        jpeg: photo.data_url,
        photo_name: photo.name
      });
      this.setStore('wizard', 'record', {
        ...this.store.wizard.record,
        jpeg: photo.data_url,
        jpeg2,
        jpeg3,
        photo_name: photo.name
      });
      this.setValue('lpcrop', '');
      this.setValue('lpjpeg', '');
      this.setStore('wizard', {
        crop_preview: '',
        selected_photo_name: name
      });
      return;
    }
    if (name === w.record.photo_name) {
      this.setStore('wizard', 'selected_photo_name', name);
      return;
    }
    const {
      jpeg2,
      jpeg3
    } = await this.createPreviews(photo.data_url);
    const updated = await this.fileAction('SET_MAIN_PHOTO', {
      name,
      jpeg2,
      jpeg3
    });
    if (!updated || !this.store.wizard) return;
    this.setValue('lpcrop', '');
    this.setValue('lpjpeg', '');
    this.setStore('wizard', 'crop_preview', '');
    this.setStore('wizard', 'selected_photo_name', name);
    await this.loadWizardPhotos();
  }

  /** The package is already imported: resume its DRAFT, or point the operator at the record. */
  adoptExisting(record) {
    if (!record) return;
    if (record.state === 'DRAFT') {
      this.openWizard(record);
      this.setStore('wizard', 'notice', `${this.tr('wizard.ResumedExisting')}: ${record.id}`);
    } else {
      this.setStore('wizard', 'error', `${this.tr('wizard.AlreadyProcessed')}: ${record.id}`);
    }
  }

  /** Builds the new wizard's working record locally; no file or workflow action leaves mOffice. */
  async prepareStagedRecord() {
    var _w$record8, _meta_upload$bytes, _device, _detected, _parsed, _parsed2;
    const w = this.store.wizard;
    if (!w || (_w$record8 = w.record) !== null && _w$record8 !== void 0 && _w$record8.id || !w.files.jpeg || !w.files.meta) return;
    const meta_upload = decodeUpload(w.files.meta);
    if (!(meta_upload !== null && meta_upload !== void 0 && (_meta_upload$bytes = meta_upload.bytes) !== null && _meta_upload$bytes !== void 0 && _meta_upload$bytes.length)) return;
    const {
      jpeg2,
      jpeg3
    } = await this.createPreviews(w.files.jpeg);
    if (!jpeg2 || !jpeg3) {
      this.setStore('wizard', 'error', this.tr('error.InvalidPhoto'));
      return;
    }
    const candidates = detectSerials(meta_upload.text);
    const devices = await this.loadDevices();
    if (!this.store.wizard) return;
    let detected = candidates[0] || null;
    let device = null;
    for (const candidate of candidates) {
      const wanted = candidate.parsed.serial.replace(/\s+/g, '').toLowerCase();
      const found = devices.find(item => item.id.replace(/\s+/g, '').toLowerCase() === wanted);
      if (found) {
        detected = candidate;
        device = found;
        break;
      }
    }
    const device_type = ((_device = device) === null || _device === void 0 ? void 0 : _device.type) || ((_detected = detected) === null || _detected === void 0 ? void 0 : _detected.type) || this.value('device_type') || this.config.device_types[0] || '';
    let parsed = parseMeta(device_type, meta_upload.text);
    if (!parsed && detected) parsed = detected.parsed;
    const parsed_values = ((_parsed = parsed) === null || _parsed === void 0 ? void 0 : _parsed.fields) || {};
    const values = _.defaults({}, w.values, parsed_values, {
      lane: '1',
      device_type,
      license_plate_number_confidence: this.config.confidence.lpn,
      country_code_confidence: this.config.confidence.country
    });
    values.device_type = device_type;
    if (device) values.device_id = device.id;
    const warnings = [...(((_parsed2 = parsed) === null || _parsed2 === void 0 ? void 0 : _parsed2.warnings) || [])];
    if (!device) {
      var _detected2, _detected2$parsed;
      let warning = '{{wf.sktzrecords.warning.DeviceNotIdentified}}';
      if ((_detected2 = detected) !== null && _detected2 !== void 0 && (_detected2$parsed = _detected2.parsed) !== null && _detected2$parsed !== void 0 && _detected2$parsed.serial) warning = `{{wf.sktzrecords.warning.DeviceNotInRegister}}: ${detected.parsed.serial}`;
      warnings.unshift(warning);
    } else if (detected && device.type !== detected.type) {
      warnings.unshift(`{{wf.sktzrecords.warning.DeviceTypeMismatch}}: ${device.type} / ${detected.type}`);
    }
    const record = {
      id: '',
      state: '',
      ...values,
      jpeg: w.files.jpeg,
      jpeg2,
      jpeg3,
      meta: meta_upload.text,
      meta_name: w.files.meta_name,
      photo_name: w.files.photo_name,
      attachments: [...w.files.photos.filter(photo => photo.name !== w.files.photo_name), ...w.files.extras].map(file => ({
        name: file.name,
        mime: file.is_jpeg ? 'image/jpeg' : file.mime || ''
      })),
      parse_warnings: warnings
    };
    this.setStore('wizard', {
      record,
      values
    });
  }

  /** Decoded byte size of a data-URL upload - base64 inflates 4:3, so the string length lies. */
  uploadBytes(data_url) {
    const str = _.toString(data_url);
    const comma = str.indexOf(',');
    const payload = comma >= 0 ? str.length - comma - 1 : str.length;
    return Math.floor(payload * 3 / 4);
  }

  /** Existing record id out of the server's AlreadyImported abort, '' otherwise. */
  alreadyImportedId(error) {
    var _exec2;
    return ((_exec2 = /AlreadyImported}}: *(\S+)/.exec(_.toString(error))) === null || _exec2 === void 0 ? void 0 : _exec2[1]) || '';
  }

  /** Human text for a server error: template errors ({{key}}: detail) via tx, keys via t. */
  errorText(e) {
    var _e$params3, _e$params4;
    const raw = _.toString((e === null || e === void 0 ? void 0 : (_e$params3 = e.params) === null || _e$params3 === void 0 ? void 0 : _e$params3.error) || (e === null || e === void 0 ? void 0 : e.message) || e);
    if (raw.includes('{{')) return this.tx(raw);
    return e !== null && e !== void 0 && (_e$params4 = e.params) !== null && _e$params4 !== void 0 && _e$params4.error ? this.t(raw) : raw;
  }

  /**
  * @description
  * Downscales the evidence photo into the stored preview (jpeg2) and table thumbnail (jpeg3).
  */
  async createPreviews(jpeg) {
    const img = new Image();
    img.src = jpeg;
    await new Promise(resolve => {
      img.onload = resolve;
      img.onerror = resolve;
      if (img.complete) resolve();
    });
    const scale = max => {
      if (!img.naturalWidth) return '';
      const ratio = Math.min(1, max.width / img.naturalWidth, max.height / img.naturalHeight);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * ratio);
      canvas.height = Math.round(img.naturalHeight * ratio);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg');
    };
    return {
      jpeg2: scale(this.config.preview.jpeg2),
      jpeg3: scale(this.config.preview.jpeg3)
    };
  }

  /**
  * @description
  * Fills the device options - read fresh on every call, because devices are added while
  * records wait on a desk. Served by the TZ workflow server (mmTzDevices proxy): dispatchers
  * have no access to the device register workflow itself. Only offline types are offered.
  */
  async loadDevices() {
    try {
      var _reply$params2;
      const reply = await this.request(this.config.workflow_server, 'mmTzDevices', {}, ['mmTzDevicesList']);
      const exclude = this.config.exclude_device_types || [];
      const device_rows = ((reply === null || reply === void 0 ? void 0 : (_reply$params2 = reply.params) === null || _reply$params2 === void 0 ? void 0 : _reply$params2.list) || []).filter(device => (device === null || device === void 0 ? void 0 : device.id) && !exclude.includes(device.type)).map(device => ({
        id: _.toString(device.id),
        type: _.toString(device.type)
      })).sort((a, b) => a.id.localeCompare(b.id, undefined, {
        numeric: true
      }));
      this.setStore({
        tm_devices: device_rows,
        devices: device_rows.map(device => device.id)
      });
      return device_rows;
    } catch (e) {
      this.error('cannot read the TM device register:', e);
      return [];
    }
  }

  // ---- bulk import ----

  openBulk() {
    this.setStore('bulk', null); // reset first — solid merges objects on set
    this.setStore('bulk', {
      entries: [],
      // all selected files; groups are rebuilt when another batch is added
      files: {},
      // metadata basename → { photos[], extras[], meta, meta_name }
      shared: {
        locality: '',
        context: ''
      },
      results: {},
      // basename → { status: 'running'|'ok'|'error', id, device_id, warnings, error }
      running: false
    });
  }
  closeBulk() {
    this.setStore('bulk', null);
  }

  /**
  * @description
  * Rebuilds the pairing map around the metadata basenames. Every file whose basename is the
  * same as the TXT basename or continues with `_...` belongs to that record. This includes
  * all JPEG frames and vendor attachments and works regardless of file/drop order.
  */
  async bulkTakeFiles(files) {
    if (!this.store.bulk || this.store.bulk.running) return;
    const additions = await this.readDropEntries(files);
    if (!this.store.bulk) return;
    const by_name = new Map((this.store.bulk.entries || []).map(entry => [entry.name.toLowerCase(), entry]));
    for (const entry of additions) by_name.set(entry.name.toLowerCase(), entry);
    const entries = [...by_name.values()];
    this.setStore('bulk', {
      entries,
      files: groupBulkEntries(entries)
    });
  }

  /** Pairing view over the bulk file map: complete pairs and the unmatched leftovers. */
  bulkPairs() {
    var _this$store$bulk;
    const files = ((_this$store$bulk = this.store.bulk) === null || _this$store$bulk === void 0 ? void 0 : _this$store$bulk.files) || {};
    const pairs = [];
    const unmatched = [];
    for (const key of Object.keys(files).sort()) {
      const entry = files[key];
      if (entry.photos.length > 0 && entry.meta) {
        const main = entry.photos[0];
        pairs.push({
          key,
          ...entry,
          jpeg: main.data_url,
          photo_name: main.name
        });
      } else {
        if (entry.meta_name) unmatched.push({
          key,
          name: entry.meta_name,
          missing: '.jpg?'
        });
        for (const photo of entry.photos) unmatched.push({
          key,
          name: photo.name,
          missing: '.txt?'
        });
        for (const extra of entry.extras) unmatched.push({
          key,
          name: extra.name,
          missing: entry.meta ? '.jpg?' : '.txt?'
        });
      }
    }
    return {
      pairs,
      unmatched
    };
  }

  /**
  * @description
  * Imports every matched pair sequentially: one IMPORT action per pair, with the shared
  * values (locality, measurement context) prefilled into each created DRAFT. Pairs that
  * already imported are skipped, so a failed run can be retried.
  */
  async runBulk() {
    const b = this.store.bulk;
    if (!b || b.running) return;
    this.setStore('bulk', 'running', true);
    for (const pair of this.bulkPairs().pairs) {
      var _this$store$bulk$resu;
      if (!this.store.bulk) return; // overlay closed mid-run
      if (/^(ok|exists)$/.test((_this$store$bulk$resu = this.store.bulk.results[pair.key]) === null || _this$store$bulk$resu === void 0 ? void 0 : _this$store$bulk$resu.status)) continue;
      this.setStore('bulk', 'results', pair.key, {
        status: 'running',
        error: ''
      });
      try {
        if (pair.photos.some(photo => this.uploadBytes(photo.data_url) > this.config.max_photo_bytes)) throw new Error(this.tr('error.InvalidPhoto'));
        if (pair.extras.some(extra => this.uploadBytes(extra.data_url) > this.config.max_file_bytes)) throw new Error(this.tr('error.InvalidAttachment'));
        const {
          jpeg2,
          jpeg3
        } = await this.createPreviews(pair.jpeg);
        const params = {
          jpeg: pair.jpeg,
          jpeg2,
          jpeg3,
          meta: pair.meta,
          meta_name: pair.meta_name,
          photo_name: pair.photo_name,
          device_type: this.config.device_types[0] || '' // same default parser as the wizard
        };
        const shared = this.store.bulk.shared;
        if (shared.locality) params.locality = shared.locality;
        if (shared.context) params.context = shared.context;
        const reply = await this.wfclient.action('', 'IMPORT', params);
        const record = _.get(reply, 'params.list[0]');
        if (!(record !== null && record !== void 0 && record.id)) throw new Error(this.tr('error.ActionFailed'));
        for (const photo of pair.photos.slice(1)) {
          await this.wfclient.action(record.id, 'ATTACH', {
            slot: 'attachment',
            name: photo.name,
            mime: 'image/jpeg',
            data: photo.data_url
          });
        }
        for (const extra of pair.extras) {
          await this.wfclient.action(record.id, 'ATTACH', {
            slot: 'attachment',
            name: extra.name,
            mime: extra.mime || '',
            data: extra.data_url
          });
        }
        this.setStore('bulk', 'results', pair.key, {
          status: 'ok',
          id: record.id,
          device_id: record.device_id || '',
          warnings: (record.parse_warnings || []).length
        });
      } catch (e) {
        var _e$params5;
        if (!this.store.bulk) return;
        // an already-imported package is reported by the server (with the record id), not duplicated
        const existing_id = this.alreadyImportedId(e === null || e === void 0 ? void 0 : (_e$params5 = e.params) === null || _e$params5 === void 0 ? void 0 : _e$params5.error);
        if (existing_id) {
          this.setStore('bulk', 'results', pair.key, {
            status: 'exists',
            id: existing_id,
            device_id: '',
            warnings: 0
          });
        } else {
          this.setStore('bulk', 'results', pair.key, {
            status: 'error',
            error: this.errorText(e)
          });
        }
      }
    }
    if (this.store.bulk) this.setStore('bulk', 'running', false);
  }

  // ---- save / confirm ----

  /** One wizard server action with busy/error handling; returns the echoed record or null. */
  async wizardAction(id, action, params) {
    this.setStore('wizard', {
      busy: action,
      error: '',
      error_raw: ''
    });
    try {
      const reply = await this.wfclient.action(id || this.generateNewId(), action, params);
      const record = _.get(reply, 'params.list[0]');
      if (!(record !== null && record !== void 0 && record.id)) throw new Error(this.tr('error.ActionFailed'));
      return record;
    } catch (e) {
      var _e$params6;
      if (this.store.wizard) this.setStore('wizard', {
        error: this.errorText(e),
        error_raw: _.toString((e === null || e === void 0 ? void 0 : (_e$params6 = e.params) === null || _e$params6 === void 0 ? void 0 : _e$params6.error) || '')
      });
      return null;
    } finally {
      if (this.store.wizard) this.setStore('wizard', 'busy', '');
    }
  }

  /** Complete locally staged package for one atomic server-side create action. */
  async stagedPackageParams() {
    var _w$record9;
    const w = this.store.wizard;
    if (!w || (_w$record9 = w.record) !== null && _w$record9 !== void 0 && _w$record9.id || !w.files.meta || !(w.files.photos || []).length) return null;
    const main_name = w.selected_photo_name || w.files.photo_name;
    const main = w.files.photos.find(photo => photo.name === main_name) || w.files.photos[0];
    if (!(main !== null && main !== void 0 && main.data_url)) return null;
    const {
      jpeg2,
      jpeg3
    } = await this.createPreviews(main.data_url);
    if (!jpeg2 || !jpeg3) return null;
    const files = [...w.files.photos.filter(photo => photo.name !== main.name).map(photo => ({
      name: photo.name,
      mime: 'image/jpeg',
      data: photo.data_url
    })), ...(w.files.extras || []).map(file => ({
      name: file.name,
      mime: file.mime || '',
      data: file.data_url
    }))];
    return {
      ...this.trimmedValues(w.values),
      jpeg: main.data_url,
      jpeg2,
      jpeg3,
      meta: w.files.meta,
      meta_name: w.files.meta_name,
      photo_name: main.name,
      files
    };
  }

  /** Creates a DRAFT or directly submits a complete local package; no per-file actions. */
  async createStagedRecord(action) {
    var _this$store$wizard4;
    const params = await this.stagedPackageParams();
    if (!params) {
      this.setStore('wizard', 'error', this.tr('error.ActionFailed'));
      return null;
    }
    const record = await this.wizardAction('', action, params);
    if (record) return record;
    const existing_id = this.alreadyImportedId((_this$store$wizard4 = this.store.wizard) === null || _this$store$wizard4 === void 0 ? void 0 : _this$store$wizard4.error_raw);
    if (existing_id) this.adoptExisting(await this.wfclient.fetchFull(existing_id).catch(() => null));
    return null;
  }

  /** Saves the wizard values into the DRAFT. */
  async saveDraft(close) {
    const w = this.store.wizard;
    if (!(w !== null && w !== void 0 && w.record)) return false;
    let updated;
    if (w.record.id) updated = await this.wizardAction(w.record.id, 'UPDATE', this.trimmedValues(w.values));else updated = await this.createStagedRecord('IMPORT');
    if (!updated) return false;
    this.setStore('wizard', 'record', updated);
    if (close) {
      const id = updated.id;
      this.closeWizard();
      this.setStore('selected', id);
    }
    return true;
  }

  /**
  * @description
  * Saves and confirms: the server validates the complete record (GIS geocode, passage build)
  * and forwards it to IS TM. Validation failures come back as translated errors and the wizard
  * stays open for correction.
  */
  async confirmSend() {
    const w = this.store.wizard;
    if (!(w !== null && w !== void 0 && w.record)) return;
    if (!w.record.id) {
      const submitted = await this.createStagedRecord('SUBMIT');
      if (!submitted) return;
      const id = submitted.id;
      this.closeWizard();
      this.setStore('selected', id);
      return;
    }
    if (!(await this.saveDraft(false))) return;
    const confirmed = await this.wizardAction(w.record.id, 'CONFIRM', {});
    if (!confirmed) return; // error shown; wizard stays open
    const id = confirmed.id;
    this.closeWizard();
    this.setStore('selected', id);
  }

  /**
  * @description
  * Downloads one attachment through GET_FILE - attachment bytes never ride a record fetch.
  */
  async downloadFile(record, file) {
    try {
      var _reply$params3, _reply$params3$list, _reply$params3$list$;
      const reply = await this.request(this.config.workflow_server, 'mmAction', {
        action: 'GET_FILE',
        id: record.id,
        params: {
          name: file.name
        }
      });
      const binary = reply === null || reply === void 0 ? void 0 : (_reply$params3 = reply.params) === null || _reply$params3 === void 0 ? void 0 : (_reply$params3$list = _reply$params3.list) === null || _reply$params3$list === void 0 ? void 0 : (_reply$params3$list$ = _reply$params3$list[0]) === null || _reply$params3$list$ === void 0 ? void 0 : _reply$params3$list$.export;
      if (binary && binary.length > 0 && typeof binary !== 'string') {
        const blob = new Blob([binary], {
          type: file.mime || 'application/octet-stream'
        });
        this.saveBlob(blob, file.name);
      } else {
        this.alertModal(file.name, this.t(_.toString(binary || 'wf.sktzrecords.error.AttachmentNotFound')));
      }
    } catch (e) {
      var _e$params7;
      this.alertModal(file.name, _.toString((e === null || e === void 0 ? void 0 : (_e$params7 = e.params) === null || _e$params7 === void 0 ? void 0 : _e$params7.error) || (e === null || e === void 0 ? void 0 : e.message) || e));
    }
  }

  /** Downloads a photo or metadata value already present in the full workflow record. */
  async downloadStoredFile(name, mime, value) {
    try {
      if (value == null || value === '') throw new Error('wf.sktzrecords.error.AttachmentNotFound');
      let blob;
      if (typeof value === 'string' && value.startsWith('data:')) {
        blob = await (await fetch(value)).blob();
      } else {
        let content = value;
        if ((value === null || value === void 0 ? void 0 : value.type) === 'Buffer' && Array.isArray(value.data)) content = new Uint8Array(value.data);else if (Array.isArray(value)) content = new Uint8Array(value);
        blob = new Blob([content], {
          type: mime || 'application/octet-stream'
        });
      }
      this.saveBlob(blob, name);
    } catch (e) {
      var _e$params8;
      this.alertModal(name, this.t(_.toString((e === null || e === void 0 ? void 0 : (_e$params8 = e.params) === null || _e$params8 === void 0 ? void 0 : _e$params8.error) || (e === null || e === void 0 ? void 0 : e.message) || e)));
    }
  }

  /** Starts a browser download and releases its temporary object URL. */
  saveBlob(blob, name) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
  * @description
  * Downloads the exact SavePassageDataRequest body for one record (EXPORT_TRM action).
  */
  async exportTrmLocal(id) {
    try {
      var _reply$params4, _reply$params4$list, _reply$params4$list$;
      const reply = await this.request(this.config.workflow_server, 'mmAction', {
        action: 'EXPORT_TRM',
        id
      });
      const binary = reply === null || reply === void 0 ? void 0 : (_reply$params4 = reply.params) === null || _reply$params4 === void 0 ? void 0 : (_reply$params4$list = _reply$params4.list) === null || _reply$params4$list === void 0 ? void 0 : (_reply$params4$list$ = _reply$params4$list[0]) === null || _reply$params4$list$ === void 0 ? void 0 : _reply$params4$list$.export;
      if (binary && binary.length > 0 && typeof binary !== 'string') {
        const blob = new Blob([binary], {
          type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = id + '.json';
        a.click();
        URL.revokeObjectURL(url);
      } else {
        this.alertModal(this.tr('action.EXPORT_TRM'), binary || this.tr('error.TrmTransformationFailed'));
      }
    } catch (e) {
      var _e$params9;
      this.alertModal(this.tr('action.EXPORT_TRM'), _.toString((e === null || e === void 0 ? void 0 : (_e$params9 = e.params) === null || _e$params9 === void 0 ? void 0 : _e$params9.error) || (e === null || e === void 0 ? void 0 : e.message) || e));
    }
  }
} // mmodOfficeWfTzRecords